import { create } from 'zustand';
import { getAlarms, getAlarmStats, getActiveAlarms } from '@/api/alarms';
import type { AlarmWithEvent } from '@/types/alarm';
import type { AlarmStats } from '@/types/api';

interface AlarmState {
  alarms: AlarmWithEvent[];
  alarmStats: AlarmStats | null;
  selectedAlarm: string | null;
  isLoading: boolean;
  pollingInterval: ReturnType<typeof setInterval> | null;
  pollingSiteId: string | null;
  generation: number;
  reset: () => void;

  fetchAlarms: (siteId?: string) => Promise<void>;
  fetchAlarmStats: (siteId?: string) => Promise<void>;
  fetchActiveAlarms: () => Promise<void>;
  setSelectedAlarm: (id: string | null) => void;
  startPolling: (siteId?: string) => void;
  stopPolling: () => void;
  handleWsMessage: (message: { type: string; data: Record<string, unknown> }) => void;
}

export const useAlarmStore = create<AlarmState>((set, get) => ({
  alarms: [],
  alarmStats: null,
  selectedAlarm: null,
  isLoading: false,
  pollingInterval: null,
  pollingSiteId: null,
  generation: 0,
  reset: () => {
    get().stopPolling();
    set((s) => ({ alarms: [], alarmStats: null, selectedAlarm: null, isLoading: false,
      pollingSiteId: null, generation: s.generation + 1 }));
  },

  fetchAlarms: async (siteId) => {
    const generation = get().generation;
    set({ isLoading: true });
    try {
      const data = await getAlarms(siteId ? { site_id: siteId } : undefined);
      if (get().generation !== generation) return;
      set({ alarms: data.items, isLoading: false });
    } catch {
      if (get().generation !== generation) return;
      set({ isLoading: false });
    }
  },

  fetchAlarmStats: async (siteId) => {
    const generation = get().generation;
    try {
      const stats = await getAlarmStats(siteId);
      if (get().generation !== generation) return;
      set({ alarmStats: stats });
    } catch {
      // silently fail
    }
  },

  fetchActiveAlarms: async () => {
    const generation = get().generation;
    try {
      const data = await getActiveAlarms();
      if (get().generation !== generation) return;
      set({ alarms: data.items });
    } catch {
      // silently fail
    }
  },

  setSelectedAlarm: (id) => set({ selectedAlarm: id }),

  startPolling: (siteId) => {
    const existing = get().pollingInterval;
    if (existing) clearInterval(existing);
    // Fetch immediately
    get().fetchAlarms(siteId);
    get().fetchAlarmStats(siteId);
    // Then poll every 30 seconds
    const interval = setInterval(() => {
      get().fetchAlarms(siteId);
      get().fetchAlarmStats(siteId);
    }, 30000);
    set({ pollingInterval: interval, pollingSiteId: siteId ?? null });
  },

  stopPolling: () => {
    const interval = get().pollingInterval;
    if (interval) {
      clearInterval(interval);
      set({ pollingInterval: null });
    }
  },

  handleWsMessage: (message) => {
    const { type, data } = message;
    if (type === 'alarm_new' || type === 'alarm_update') {
      // WS notifications contain alarm_id, not the complete REST alarm model.
      if (typeof (data.alarm_id ?? data.id) !== 'string' || !get().pollingSiteId) return;
      void get().fetchAlarms(get().pollingSiteId!);
      void get().fetchAlarmStats(get().pollingSiteId!);
    }
  },
}));
